import React, { useState, useEffect, useCallback, useRef } from 'react';
import { trainingPlanApi } from '../lib/api';
import { useUnit } from '../contexts/UnitContext';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { HR_MONTHS, currentFy } from '../lib/utils';

const MONTH_FULL = { Apr: 'April', May: 'May', Jun: 'June', Jul: 'July', Aug: 'August', Sep: 'September',
  Oct: 'October', Nov: 'November', Dec: 'December', Jan: 'January', Feb: 'February', Mar: 'March' };

// 'Apr' of FY 2026-27 -> '2026-04'. Jan..Mar belong to the next calendar year.
const ymOf = (fy, m) => {
  const start = parseInt(fy.slice(0, 4), 10);
  const idx = HR_MONTHS.indexOf(m);
  const y = idx <= 8 ? start : start + 1;
  return `${y}-${String(idx <= 8 ? idx + 4 : idx - 8).padStart(2, '0')}`;
};
// FY 2026-27 -> '2025-2026', the label the baseline row carries on the sheet.
const prevFyLabel = (fy) => {
  const start = parseInt(fy.slice(0, 4), 10);
  return `${start - 1}-${start}`;
};

const COLS = [
  { key: 'staff', label: 'Staff' },
  { key: 'associates', label: 'Associates' },
];


export default function TrainingPlan() {
  // These registers are per plant: one unit's entries must never
  // appear on, or score against, another.
  const { activeUnit } = useUnit();
  const [data, setData] = useState(null);
  const [fySel, setFySel] = useState(currentFy());
  // Figures being typed, keyed by YYYY-MM. A month is only in here while it is
  // being edited, so an untouched row shows exactly what is saved.
  const [edit, setEdit] = useState({});
  const [savingYm, setSavingYm] = useState('');

  // A plant switch leaves the previous read in flight; without this guard the
  // slower reply wins and the register shows another plant's months.
  const wanted = useRef(activeUnit);
  const load = useCallback(async () => {
    wanted.current = activeUnit;
    try {
      const r = await trainingPlanApi.list(activeUnit);
      if (wanted.current === activeUnit) setData(r.data);
    }
    catch (e) { if (wanted.current === activeUnit) toast.error('Failed to load the training register'); }
  }, [activeUnit]);
  useEffect(() => { load(); }, [load]);

  const setCell = (ym, field, v) =>
    setEdit((s) => ({ ...s, [ym]: { ...(s[ym] || {}), [field]: v } }));

  const cellValue = (ym, field, row) => {
    const e = edit[ym];
    if (e && e[field] !== undefined) return e[field];
    return row ? String(row[field] ?? '') : '';
  };
  const rowDirty = (ym, row) => {
    const e = edit[ym];
    if (!e) return false;
    return COLS.some(({ key }) =>
      e[key] !== undefined && String(e[key]) !== String(row ? (row[key] ?? '') : ''));
  };

  const saveMonth = async (ym, row) => {
    const vals = COLS.map(({ key }) => cellValue(ym, key, row));
    if (vals.some((v) => v === '')) {
      // A blank is not a zero: an unrecorded month stays unscored, while a
      // recorded zero is a month that trained nobody. Asking for both keeps
      // the two apart.
      toast.error('Enter both figures. Put 0 where nobody was trained.');
      return;
    }
    setSavingYm(ym);
    try {
      const body = { ym, unit: activeUnit || '' };
      COLS.forEach(({ key }, i) => { body[key] = Number(vals[i]); });
      const r = await trainingPlanApi.saveMonth(body);
      toast.success(`${MONTH_FULL[r.data.month]} saved — ${r.data.total} training(s)`
        + (r.data.replaced ? ' (previous figures kept in history)' : ''));
      setEdit((s) => { const n = { ...s }; delete n[ym]; return n; });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSavingYm(''); }
  };

  const clearMonth = async (ym, label) => {
    if (!window.confirm(`Clear ${label}? That month goes back to having nothing recorded, `
      + `which is not the same as a recorded zero.`)) return;
    setSavingYm(ym);
    try {
      await trainingPlanApi.deleteMonth(ym, activeUnit || '');
      setEdit((s) => { const n = { ...s }; delete n[ym]; return n; });
      toast.success(`${label} cleared`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not clear'); }
    finally { setSavingYm(''); }
  };

  const numCell = 'w-full bg-transparent text-right outline-none px-1 py-0.5 rounded '
    + 'focus:bg-white focus:ring-2 focus:ring-[#C7D7FE] text-[#11182B]';

  const fys = [...new Set([currentFy(), ...(data?.rows || []).map((r) => r.fy)])].sort().reverse();
  const byYm = Object.fromEntries((data?.rows || []).map((r) => [r.ym, r]));
  const months = HR_MONTHS.map((m) => ({ m, ym: ymOf(fySel, m), r: byYm[ymOf(fySel, m)] }));
  const filled = months.filter((x) => x.r);
  const total = (f) => filled.reduce((s, x) => s + Number(x.r[f] || 0), 0);
  // The plan comes from the server, computed by the same function the KPI uses,
  // so this row and the mark can never quote a different plan.
  const base = data?.baselines?.[fySel];
  const plan = (key) => (base ? base[`${key}_plan`] : null);
  const hasPlan = COLS.some(({ key }) => plan(key) != null);

  return (
    <div className="flex h-screen app-shell">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />
        <main className="flex-1 overflow-y-auto p-6 space-y-5">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-[-0.02em] text-[#172554]">Training Plan vs Actual</h1>
            <p className="text-sm text-[#5A6377]">
              Month-wise trainings delivered, counted separately for staff and associates. The plan each
              month is measured against is last year's monthly average — the <b>Average {prevFyLabel(fySel)}</b> row
              below. This register automatically scores the HR KPI <b>Training Plan vs Actual</b>: plan met = 4 marks,
              then −1 for every training short, down to 0.
            </p>
          </div>

          <div className="bg-white border border-[#E6E9F0] rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <h3 className="font-medium text-[#11182B]">Monthly register</h3>
              <select value={fySel} onChange={(e) => setFySel(e.target.value)}
                data-testid="training-fy"
                className="border border-[#E6E9F0] rounded-lg px-2 py-1 text-sm">
                {fys.map((f) => <option key={f} value={f}>FY {f}</option>)}
              </select>
              {!data && <span className="text-[#98A1B2] text-sm">Loading…</span>}
              <span className="text-xs text-[#5A6377] ml-auto">
                Type the figures straight into the table, then press <b>Enter</b> or click <b>Save</b>.
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-[#C9CFDC]" style={{ borderCollapse: 'collapse' }}>
                <thead>
                  <tr className="bg-[#EFF1F6] text-[#11182B] text-xs uppercase">
                    <th className="py-2 px-3 text-left border border-[#C9CFDC]">Month</th>
                    {COLS.map(({ key, label }) => (
                      <th key={key} className="py-2 px-3 text-right border border-[#C9CFDC]">{label}</th>
                    ))}
                    {/* No heading: this column is the row's actions, not a
                        figure. The mark it used to print lived on the KPI as
                        well, and two copies of one number is how they come to
                        disagree. */}
                    <th className="py-2 px-3 border border-[#C9CFDC]" />
                  </tr>
                </thead>
                <tbody>
                  {/* The plan: last year's monthly average of each column. */}
                  <tr className="bg-[#F5F8FF] font-semibold text-[#172554]">
                    <td className="py-2 px-3 border border-[#C9CFDC]">
                      Average {prevFyLabel(fySel)}
                      <span className="block text-[10px] font-normal text-[#5A6377] normal-case">
                        the plan every month below is held to
                      </span>
                    </td>
                    {COLS.map(({ key }) => (
                      <td key={key} className="py-2 px-3 border border-[#C9CFDC] text-right">
                        {plan(key) == null ? <span className="text-[#98A1B2]">—</span> : plan(key)}
                        {base && base[key] != null && plan(key) !== base[key] && (
                          <span className="block text-[10px] font-normal text-[#98A1B2]">
                            avg {base[key].toFixed(2)}
                          </span>
                        )}
                      </td>
                    ))}
                    <td className="py-2 px-3 border border-[#C9CFDC]"></td>
                  </tr>
                  {months.map(({ m, ym, r }) => {
                    const dirty = rowDirty(ym, r);
                    return (
                      <tr key={ym} className={dirty ? 'bg-[#FFFBEB]' : ''}>
                        <td className="py-1.5 px-3 border border-[#E6E9F0] font-medium text-[#11182B]">
                          {MONTH_FULL[m]}
                          {r && (r.history || []).length > 0 &&
                            <span className="text-[10px] text-[#98A1B2]"> · {r.history.length} rev</span>}
                        </td>
                        {COLS.map(({ key }) => {
                          const v = cellValue(ym, key, r);
                          const short = hasPlan && v !== '' && Number(v) < (plan(key) || 0);
                          return (
                            <td key={key} className="py-1.5 px-2 border border-[#E6E9F0]">
                              <div className="flex items-center justify-end gap-1">
                                <input type="number" min="0" step="1" value={v} placeholder="—"
                                  data-testid={`training-${key}-${ym}`}
                                  onChange={(ev) => setCell(ym, key, ev.target.value)}
                                  onKeyDown={(ev) => ev.key === 'Enter' && dirty && saveMonth(ym, r)}
                                  className={numCell} />
                                <span className="w-10 text-[10px] text-[#B42318] text-right">
                                  {short ? `−${(plan(key) || 0) - Number(v)}` : ''}
                                </span>
                              </div>
                            </td>
                          );
                        })}
                        <td className="py-1.5 px-3 border border-[#E6E9F0] text-right font-semibold">
                          <div className="flex items-center justify-end gap-2">
                            {dirty ? (
                              <button onClick={() => saveMonth(ym, r)} disabled={savingYm === ym}
                                data-testid={`training-save-${ym}`}
                                className="text-[11px] font-semibold bg-[#15A36E] text-white rounded px-2 py-0.5 hover:bg-[#12855A] disabled:opacity-60">
                                {savingYm === ym ? 'Saving…' : 'Save'}
                              </button>
                            ) : r ? (
                              <button onClick={() => clearMonth(ym, MONTH_FULL[m])} disabled={savingYm === ym}
                                title="Clear this month" data-testid={`training-clear-${ym}`}
                                className="text-[11px] text-[#98A1B2] hover:text-[#C2151F] font-normal">clear</button>
                            ) : <span className="w-8" />}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="bg-[#F7F8FB] font-bold text-[#172554]">
                    <td className="py-2 px-3 border border-[#C9CFDC]">Total</td>
                    {COLS.map(({ key }) => (
                      <td key={key} className="py-2 px-3 border border-[#C9CFDC] text-right">
                        {filled.length ? total(key) : '—'}
                      </td>
                    ))}
                    <td className="py-2 px-3 border border-[#C9CFDC]"></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-[#98A1B2] mt-2">
              {hasPlan
                ? <>A training is a whole thing, so the plan is last year's average rounded to a whole
                    training. Staff and associates are counted separately on purpose — a surplus of staff
                    training does not cancel out associates who were never trained.</>
                : <>No <b>FY {prevFyLabel(fySel)}</b> months are on file, so there is no plan to measure against
                    and the KPI stays unscored — never guessed. Switch the year above and enter last year's
                    months first.</>}
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
